/**
 * DocuSignDocs — public, always-available integration documentation page.
 *
 * Purpose: Referenced from the DocuSign Go-Live verification form so
 * reviewers can validate what iSpeedToClose's integration does, how it
 * handles OAuth, what data it touches, and how to contact us.
 *
 * Design: Airbnb/Notion white with premium typography, generous
 * whitespace, and left-aligned reading flow.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield, Zap, FileSignature, Lock, Mail, Globe,
  Check, ArrowRight, ExternalLink, Code2, Users,
} from "lucide-react";

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`docs-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <Icon size={18} className="text-emerald-600" />
        <h2 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h2>
      </div>
      <div className="text-slate-700 leading-relaxed">{children}</div>
    </div>
  );
}

export default function DocuSignDocs() {
  useEffect(() => {
    document.title = "DocuSign Integration · iSpeedToClose";
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="docusign-docs-page">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="docs-home-link">
            <div className="h-7 w-7 rounded bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">
              iS
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-slate-900">iSpeedToClose</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Integration Docs</div>
            </div>
          </a>
          <a
            href="mailto:tperrin@perrinweb.com"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900"
            data-testid="docs-contact"
          >
            <Mail size={13} />
            Contact
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-700 font-bold mb-4">
          Third-Party Integration Documentation
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl leading-[1.05] tracking-tight text-slate-900">
          DocuSign eSignature Integration
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
          iSpeedToClose is a real estate operations platform used by Perrin
          Properties, LLC to manage deal pipelines, generate legally-binding
          contracts, and coordinate document execution between sellers,
          buyers, and title agents.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium">
            <Check size={12} /> Private Custom Integration
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 font-medium">
            OAuth 2.0 + JWT Grant
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 font-medium">
            REST API v2.1
          </span>
        </div>
      </section>

      {/* Body sections */}
      <main className="max-w-4xl mx-auto px-5 md:px-8 pb-24 space-y-6">
        <SectionCard icon={Zap} title="What the Integration Does">
          <p className="mb-4">
            The integration automates the creation, delivery, and secure
            e-signature execution of real estate contracts inside our
            property management workflow. Every envelope sent has a full
            audit trail synced back to our platform's immutable timeline.
          </p>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Contract types sent:</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Purchase Agreements</li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Assignment of Contract</li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Joint Venture Agreements</li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Closing Addenda</li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Assignment Fee Agreements</li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Option Contracts</li>
            </ul>
          </div>
        </SectionCard>

        <SectionCard icon={Lock} title="Authentication & Authorization">
          <p className="mb-3">
            We support two DocuSign authentication flows, both using DocuSign's
            approved patterns:
          </p>
          <ol className="space-y-3 mt-4">
            <li>
              <span className="font-semibold text-slate-900">1. JWT User Application (server-to-server)</span>
              <div className="text-sm text-slate-600 ml-4 mt-1">
                Used for our internal service account operations. Scopes requested:
                <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs">signature</code>
                <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs">impersonation</code>
              </div>
            </li>
            <li>
              <span className="font-semibold text-slate-900">2. OAuth 2.0 Authorization Code Grant</span>
              <div className="text-sm text-slate-600 ml-4 mt-1">
                Used when authorized team members connect their own DocuSign
                accounts. Each user's refresh token is stored encrypted, rotated
                automatically 60 seconds before expiry, and revoked immediately
                on disconnect.
              </div>
            </li>
          </ol>
        </SectionCard>

        <SectionCard icon={Code2} title="API Endpoints Called">
          <div className="grid grid-cols-1 gap-2 text-sm font-mono">
            <div><code className="text-emerald-700">POST</code> /v2.1/accounts/{`{accountId}`}/envelopes — create + send</div>
            <div><code className="text-blue-700">GET</code> /v2.1/accounts/{`{accountId}`}/envelopes/{`{id}`} — poll status</div>
            <div><code className="text-blue-700">GET</code> /v2.1/accounts/{`{accountId}`}/envelopes/{`{id}`}/documents/combined — download signed PDF</div>
            <div><code className="text-amber-700">PUT</code> /v2.1/accounts/{`{accountId}`}/envelopes/{`{id}`} — void or resend</div>
            <div><code className="text-blue-700">GET</code> /v2.1/accounts/{`{accountId}`}/templates — list user's templates</div>
            <div><code className="text-emerald-700">POST</code> /v2.1/accounts/{`{accountId}`}/connect — auto-provision webhook config</div>
            <div><code className="text-blue-700">GET</code> /oauth/userinfo — verify token + resolve base URI</div>
          </div>
        </SectionCard>

        <SectionCard icon={Shield} title="Data Security & Handling">
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> All DocuSign communication over HTTPS/TLS 1.3</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Access tokens cached in memory only; refresh tokens encrypted at rest in MongoDB</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Private RSA key stored in secure environment variables — never in source control</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> DocuSign Connect webhook payloads validated with HMAC-SHA256 signatures</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Signed documents stored per Perrin Properties&apos; retention policy — no third-party access</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> No SSN or financial account numbers collected via DocuSign tabs</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Every envelope send is logged to an immutable audit timeline</li>
          </ul>
        </SectionCard>

        <SectionCard icon={FileSignature} title="Envelope Status & Webhooks">
          <p className="mb-3">
            Envelope status updates flow back to iSpeedToClose in two ways:
          </p>
          <ol className="space-y-3 text-sm">
            <li>
              <strong>Primary:</strong> DocuSign Connect webhooks push HMAC-signed
              status events to <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-xs">POST /api/docusign/webhook</code>.
              Auto-provisioned per user via <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-xs">POST /connect</code>.
            </li>
            <li>
              <strong>Fallback:</strong> User-triggered polling via
              <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-xs">GET /envelopes/{`{id}`}</code>
              refreshes local state when needed.
            </li>
          </ol>
        </SectionCard>

        <SectionCard icon={Users} title="Who Signs the Documents">
          <p className="mb-3">Recipients on our envelopes fall into these categories:</p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Property sellers (individuals or trusts)</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Cash buyers / real estate investors</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Joint venture partners</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Title agents / escrow officers</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Attorneys (occasional CC recipients)</li>
          </ul>
          <p className="mt-4 text-sm text-slate-600">
            Signer authentication: Standard email-based recipient authentication
            (DocuSign default). Signers may be required to complete Access Code
            or SMS authentication on a per-template basis when configured by
            the sending user.
          </p>
        </SectionCard>

        <SectionCard icon={Globe} title="Error Handling & Reliability">
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> All DocuSign responses logged with trace tokens for support diagnostics</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> 401 Unauthorized triggers one automatic token refresh + single retry</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Failed sends surface DocuSign&apos;s <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">errorCode</code> and <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">message</code> directly in the UI</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> 429 rate-limited responses back off exponentially</li>
            <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" /> Batch operations executed concurrently to stay under upstream timeout ceilings</li>
          </ul>
        </SectionCard>

        {/* Contact */}
        <div className="rounded-xl border-2 border-slate-900 bg-slate-900 p-6 md:p-8 text-white">
          <h2 className="text-lg font-bold mb-3">Integration Contact</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-400">Organization:</span> Perrin Properties, LLC</div>
            <div><span className="text-slate-400">Contact:</span> Tracy Perrin</div>
            <div><span className="text-slate-400">Email:</span> <a href="mailto:tperrin@perrinweb.com" className="text-emerald-400 hover:underline">tperrin@perrinweb.com</a></div>
            <div><span className="text-slate-400">Website:</span> <a href="https://ispeedtoclose.com" className="text-emerald-400 hover:underline">ispeedtoclose.com</a></div>
            <div><span className="text-slate-400">Integration Key:</span> <code className="text-slate-300 text-xs">fd01c390-69eb-4613-a231-aad3ed6aa312</code></div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 px-5 md:px-8 py-8">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} Perrin Properties, LLC. All rights reserved.</div>
          <Link to="/" className="inline-flex items-center gap-2 hover:text-slate-900 transition-colors">
            iSpeedToClose home <ArrowRight size={12} />
          </Link>
        </div>
      </footer>
    </div>
  );
}
